import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { mirror } from "../lib/api";
import { Silhouette } from "../components/Silhouette";
import { useFutureSelf } from "../lib/futureSelf";
import { ShareCardButton } from "../components/ShareCardButton";

const MorphFace = ({ from, to, size }) => (
  <div className="relative rounded-full overflow-hidden" style={{ width: size, height: size, boxShadow: "0 0 50px rgba(255,215,0,0.5)" }} data-testid="mirror-morph">
    <motion.img
      src={from}
      alt="you today"
      className="absolute inset-0 w-full h-full object-cover"
      animate={to ? { opacity: 0, scale: 1.04 } : { opacity: [1, 0.75, 1] }}
      transition={to ? { duration: 4.5, ease: "easeInOut" } : { duration: 2, repeat: Infinity }}
    />
    {to && (
      <motion.img
        src={to}
        alt="future you"
        className="absolute inset-0 w-full h-full object-cover"
        initial={{ opacity: 0, filter: "blur(10px) brightness(1.6)" }}
        animate={{ opacity: 1, filter: "blur(0px) brightness(1)" }}
        transition={{ duration: 4.5, ease: "easeInOut" }}
        data-testid="mirror-morph-aged"
      />
    )}
    <motion.div
      className="absolute inset-0 pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(255,215,0,0.35), transparent 70%)" }}
      animate={{ opacity: [0, 0.8, 0] }}
      transition={{ duration: 3, repeat: Infinity }}
    />
  </div>
);

const Face = ({ src, size, older }) =>
  src ? (
    <img
      src={src}
      alt={older ? "future you" : "you today"}
      className="rounded-full object-cover"
      style={{ width: size * 1.1, height: size * 1.1, boxShadow: "0 0 40px rgba(255,215,0,0.45)" }}
      data-testid={older ? "mirror-aged-photo" : "mirror-photo"}
    />
  ) : (
    <Silhouette size={size} color="#FFD700" older={older} />
  );

export default function Mirror() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState(0);
  const [subtitleIdx, setSubtitleIdx] = useState(0);
  const fs = useFutureSelf(id);
  const { ensureAged, years } = fs;

  useEffect(() => { ensureAged(); }, [ensureAged]);

  useEffect(() => {
    (async () => {
      const d = await mirror(id, years).catch(() => null);
      setData(d || { speech: "You are enough. Keep walking.", signature_line: "Begin anyway.", age_delta: years });
    })();
  }, [id, years]);

  const morphReady = !!fs.journey && (!fs.photoUrl || !!fs.agedUrl || fs.agingError);

  useEffect(() => {
    if (stage === 0) {
      const t = setTimeout(() => setStage(1), 2500);
      return () => clearTimeout(t);
    }
    if (stage === 1) {
      const t = setTimeout(() => setStage(2), morphReady ? (fs.agedUrl ? 6000 : 2200) : 30000);
      return () => clearTimeout(t);
    }
  }, [stage, morphReady, fs.agedUrl]);

  const sentences = data ? data.speech.split(/(?<=[.!?])\s+/).filter(Boolean) : [];

  useEffect(() => {
    if (stage !== 2 || sentences.length === 0) return;
    const t = setInterval(() => {
      setSubtitleIdx((i) => (i + 1 < sentences.length ? i + 1 : i));
    }, 3800);
    return () => clearInterval(t);
  }, [stage, sentences.length]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 relative overflow-hidden">
      {/* Mirror glow backdrop */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(255,215,0,0.15) 0%, rgba(176,38,255,0.08) 25%, transparent 60%)",
        }}
      />

      <div className="relative w-full max-w-3xl text-center">
        <AnimatePresence mode="wait">
          {stage === 0 && (
            <motion.div
              key="s0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4 }}
            >
              <div className="font-mono-label text-white/50 mb-6">A glowing mirror appears</div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: "easeOut" }}
                className="relative mx-auto"
                style={{
                  width: 280,
                  height: 380,
                  borderRadius: "50% 50% 44% 44% / 54% 54% 44% 44%",
                  border: "3px solid rgba(255,215,0,0.65)",
                  background: "radial-gradient(ellipse at center, rgba(255,215,0,0.08), rgba(176,38,255,0.12), transparent 70%)",
                  boxShadow: "0 0 60px rgba(255,215,0,0.4), 0 0 120px rgba(176,38,255,0.3), inset 0 0 60px rgba(0,240,255,0.1)",
                }}
                data-testid="mirror-frame"
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <Face src={fs.photoUrl} size={160} />
                </div>
              </motion.div>
            </motion.div>
          )}

          {stage === 1 && (
            <motion.div
              key="s1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
            >
              <div className="font-mono-label text-amber-300/80 mb-6">
                {morphReady ? "The reflection transforms" : "The reflection is changing…"}
              </div>
              <motion.div
                className="relative mx-auto"
                style={{
                  width: 280,
                  height: 380,
                  borderRadius: "50% 50% 44% 44% / 54% 54% 44% 44%",
                  border: "3px solid rgba(255,215,0,0.85)",
                  boxShadow: "0 0 80px rgba(255,215,0,0.55), 0 0 160px rgba(176,38,255,0.4)",
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  {fs.photoUrl ? (
                    <MorphFace from={fs.photoUrl} to={morphReady ? fs.agedUrl : null} size={187} />
                  ) : (
                    <Face size={170} older />
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}

          {stage === 2 && data && (
            <motion.div
              key="s2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2 }}
            >
              <div className="font-mono-label text-amber-300/80 mb-4">
                A message from you · {fs.futureAgeLabel(data.age_delta || years)}
              </div>
              <div
                className="mx-auto mb-10 relative"
                style={{
                  width: 220,
                  height: 300,
                  borderRadius: "50% 50% 44% 44% / 54% 54% 44% 44%",
                  border: "2px solid rgba(255,215,0,0.75)",
                  boxShadow: "0 0 50px rgba(255,215,0,0.4), 0 0 100px rgba(176,38,255,0.3)",
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <Face src={fs.agedUrl || fs.photoUrl} size={120} older />
                </div>
              </div>

              <div className="min-h-[80px] mb-8" data-testid="mirror-subtitle">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={subtitleIdx}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.6 }}
                    className="text-xl md:text-2xl font-display font-light leading-relaxed text-white/90 max-w-2xl mx-auto"
                  >
                    {sentences[subtitleIdx]}
                  </motion.p>
                </AnimatePresence>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="font-display italic text-amber-300 text-lg mb-10 text-glow-gold"
                data-testid="mirror-signature"
              >
                "{data.signature_line}"
              </motion.div>

              <div className="flex gap-3 justify-center flex-wrap">
                {fs.photoUrl && (
                  <ShareCardButton fs={fs} message={data.signature_line || data.speech} years={data.age_delta || years} testId="mirror-share-btn" />
                )}
                <button
                  onClick={() => navigate(`/journey/${id}`)}
                  className="btn-pill btn-pill-gold"
                  data-testid="mirror-continue-btn"
                >
                  Walk back into the simulation <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!data && stage === 0 && (
          <div className="mt-10 text-white/50 flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={14} /> The mirror is waking…
          </div>
        )}
      </div>
    </div>
  );
}
